import React, { useCallback, useRef, useEffect, useMemo } from 'react';

import classNames from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { DatePicker } from 'ui-components/components/date-picker';
import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { testId } from 'ui-components/services/testid-service';

import { useSelector } from 'react-redux';
import Global from '../../../../store/global-selectors';
import { shouldShowDatePicker, shouldShowTwoDatePickers, shouldShowNumberInput } from './form-date-helper';
import { DateService } from '../../../../services/date-service';

import style from '../common.module.less';

const adaptDateToFormat = (date, fromFormat, toFormat) => moment(date, fromFormat).format(toFormat);

export const FormDateComparison = React.memo(
  ({ criterionStateMethods, criterionOperatorOptions, formCriteriaClassname = '', dropdownMenuType, dateFormatString }) => {
    const requestedAnimationFrame = useRef(null);
    const singleDatePickerRef = useRef(null);
    const doubleDatePickerRef = useRef(null);
    const numberInputRef = useRef(null);

    const findRefByOption = (option) => {
      let ref;
      if (shouldShowDatePicker(option)) {
        ref = singleDatePickerRef;
      }
      if (shouldShowTwoDatePickers(option)) {
        ref = doubleDatePickerRef;
      }
      if (shouldShowNumberInput(option)) {
        ref = numberInputRef;
      }
      return ref;
    };

    const { getCriterionOperator, updateCriterionOperator, getCriterionArgument, updateCriterionArgument } = criterionStateMethods;
    const selectedOperator = getCriterionOperator();
    const selectedOperatorId = selectedOperator ? selectedOperator.id : undefined;
    const criterionArgument = useMemo(() => getCriterionArgument() || {}, [getCriterionArgument]);
    const userDateFormatString = useSelector(Global.selectUserUserDateFormat);
    const today = moment().format(userDateFormatString);
    const numberInputValue =
      DateService.isValidDateString(criterionArgument.value, dateFormatString) || typeof criterionArgument.value === 'object'
        ? ''
        : criterionArgument.value;
    const selectedDate =
      typeof criterionArgument.value === 'object' || !DateService.isValidDateString(criterionArgument.value, dateFormatString)
        ? today
        : criterionArgument.value;

    const { startRangeDate, endRangeDate } = useMemo(() => {
      if (typeof criterionArgument.value === 'object') {
        return {
          startRangeDate: adaptDateToFormat(criterionArgument.value.startRangeDate, dateFormatString, userDateFormatString),
          endRangeDate: adaptDateToFormat(criterionArgument.value.endRangeDate, dateFormatString, userDateFormatString),
        };
      }

      if (!numberInputValue && !selectedDate) {
        const adaptedValue = {
          startRangeDate: adaptDateToFormat(today, userDateFormatString, dateFormatString),
          endRangeDate: adaptDateToFormat(today, userDateFormatString, dateFormatString),
        };
        updateCriterionArgument({ value: adaptedValue });
      }
      return {
        startRangeDate: today,
        endRangeDate: today,
      };
    }, [criterionArgument, today, updateCriterionArgument, dateFormatString, userDateFormatString, numberInputValue, selectedDate]);

    const updateDatesToFormat = (startDate, endDate) => {
      const adaptedStartDate = moment(startDate, userDateFormatString).format(dateFormatString);
      const adaptedEndDate = moment(endDate, userDateFormatString).format(dateFormatString);
      updateCriterionArgument({
        value: {
          startRangeDate: adaptedStartDate,
          endRangeDate: adaptedEndDate,
        },
      });
    };

    const setStartRangeDate = (dateValue) => {
      if (!dateValue) {
        return;
      }
      updateDatesToFormat(dateValue, endRangeDate);
    };

    const setEndRangeDate = (dateValue) => {
      if (!dateValue) {
        return;
      }
      updateDatesToFormat(startRangeDate, dateValue);
    };

    const scrollElementIntoViewIfNeeded = (option) => {
      if (!requestedAnimationFrame.current) {
        requestedAnimationFrame.current = global.requestAnimationFrame(() => {
          requestedAnimationFrame.current = null;
          const ref = findRefByOption(option);
          if (ref && ref.current) {
            if (ref.current.scrollIntoView) {
              ref.current.scrollIntoView({
                behavior: 'smooth',
              });
            }
            ref.current?.focus();
          }
        });
      }
    };

    const onChange = useCallback(
      (value) => {
        const adaptedDateValue = moment(value, userDateFormatString).format(dateFormatString);
        updateCriterionArgument({ value: adaptedDateValue });
      },
      [updateCriterionArgument, userDateFormatString, dateFormatString],
    );

    const onKeyDown = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const scrollElementIntoViewAndUpdateCriterion = (option) => {
      scrollElementIntoViewIfNeeded(option);
      updateCriterionOperator(option);
    };

    useEffect(() => {
      if (requestedAnimationFrame.current) {
        global.cancelAnimationFrame(requestedAnimationFrame.current);
      }
    }, []);

    return (
      <div className={classNames(style['form-criteria-section'], formCriteriaClassname)}>
        <div>
          {criterionOperatorOptions.map((option, index) => (
            <DropdownMenuItem
              key={index}
              dropdownMenuType={dropdownMenuType}
              onClick={() => scrollElementIntoViewAndUpdateCriterion(option)}
              selected={option.id === selectedOperatorId}
            >
              {option.displayName}
            </DropdownMenuItem>
          ))}
        </div>
        {shouldShowDatePicker(selectedOperator) && (
          <DatePicker
            allowPastDates
            className={style['date-picker']}
            dateFormat={userDateFormatString}
            innerRef={singleDatePickerRef}
            onChange={onChange}
            shouldInputHaveFullWidth
            value={selectedDate}
          />
        )}
        {shouldShowTwoDatePickers(selectedOperator) && (
          <>
            <DatePicker
              allowPastDates
              className={style['date-picker']}
              dateFormat={userDateFormatString}
              onChange={setStartRangeDate}
              shouldInputHaveFullWidth
              value={startRangeDate}
            />
            <DatePicker
              allowPastDates
              className={style['date-picker']}
              dateFormat={userDateFormatString}
              innerRef={doubleDatePickerRef}
              onChange={setEndRangeDate}
              shouldInputHaveFullWidth
              value={endRangeDate}
            />
          </>
        )}
        {shouldShowNumberInput(selectedOperator) && (
          <div className={style['number-days-input']}>
            <input
              ref={numberInputRef}
              onChange={(e) => updateCriterionArgument({ value: e.target.value })}
              onKeyDown={onKeyDown}
              type='number'
              value={numberInputValue}
              {...testId('number-days-input')}
            />
            <FormattedMessage defaultMessage='days' description='formDateComparison.days' id='HDZvIn' />
          </div>
        )}
      </div>
    );
  },
);

FormDateComparison.propTypes = {
  criterionStateMethods: PropTypes.shape({
    getCriterionOperator: PropTypes.func,
    updateCriterionOperator: PropTypes.func,
    getCriterionArgument: PropTypes.func,
    updateCriterionArgument: PropTypes.func,
  }).isRequired,
  criterionOperatorOptions: PropTypes.array.isRequired,
  formCriteriaClassname: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  dateFormatString: PropTypes.string.isRequired,
};

FormDateComparison.displayName = 'FormDateComparison';
